import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useDispatch } from "react-redux";
import useApiClient from "@/api/Client";
import ENDPOINTS from "@/api/endpoints";
import { clearSelectedPlace } from "@/store/slices/selectedPlaceSlice";
import { Modal, Box, Typography, Rating, TextField, Button } from "@mui/material";
import { usePreventNavigation } from "@/hooks/usePreventNavigation";
import tick from "../../assets/tick.png";


const styles = {
      modalContainer: {
    backgroundColor: "#fff",
    borderRadius: "12px",
    padding: "24px",
    width: "90%",
    maxWidth: 400,
    boxShadow: "0 4px 20px rgba(0,0,0,0.15)",
    display: "flex",
    flexDirection: "column",
    mx: "auto",
    mt: { xs: "20%", sm: "10%" },
  },
  modalTitle: {
    fontSize: "20px",
    fontWeight: "bold",
    textAlign: "center",
    mb: 1,
  },
  modalSubtitle: {
    fontSize: "14px",
    textAlign: "center",
    color: "#666",
    mb: 2,
  },
  feedbackInput: {
    "& .MuiOutlinedInput-root": {
      borderRadius: "8px",
    },
    mb: 2,
  },
  submitFeedbackButton: {
    textTransform: "none",
    backgroundColor: "#7747FB",
    color: "#fff",
    fontWeight: 600,
    fontSize: "14px",
    padding: "12px",
    borderRadius: "8px",
    "&:hover": {
      backgroundColor: "#7747FB",
    },
  },
};

export const SuccessModal = ({
  contributionId,
  open,
  onClose
}: {
  contributionId: string;
  open: boolean;
  onClose: () => void;
  onSubmitFeedback?: (rating: number, feedback: string) => void;
}) => {
  const [rating, setRating] = useState<number | null>(null);
  const [feedback, setFeedback] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { api } = useApiClient();

  usePreventNavigation(open);

  const handleSubmit = async () => {
    if (!rating) return;

    setIsSubmitting(true);
    try {
      await handleFeedbackSubmit(rating, feedback, contributionId);
      dispatch(clearSelectedPlace());
      onClose();
      localStorage.setItem("profileTab", "contribution");
      navigate("/dashboard", { replace: true });
      setTimeout(() => {
        localStorage.removeItem("from_explore_to_contribute");
      }, 100);
    } catch (error) {
      console.error("Feedback submission error:", error);
    } finally {
      setIsSubmitting(false);
    }
  };
    const handleFeedbackSubmit = async (rating: number, feedback: string, contributionId: string) => {
    try {
      const feedbackData = {
        rating: rating,
        remarks: feedback || null,
      };

      const res = await api.post(ENDPOINTS.CONTRIBUTION.FEEDBACK(contributionId), feedbackData);
      if (res.status !== 201) {
        throw new Error("Failed to submit feedback");
      }

    } catch (error) {
      console.error("Error submitting feedback:", error);
      // Don't block the user flow if feedback fails
    }
  };

  return (
    <Modal open={open} onClose={onClose}>
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          minHeight: "100vh",
        }}
      >
        <Box sx={styles.modalContainer}>
          {/* Center the image */}
          <Box sx={{ display: "flex", justifyContent: "center", mb: 2 }}>
            <img src={tick} alt="confirm" style={{ height: 80, width: 80 }} />
          </Box>
          <Typography sx={styles.modalTitle}>Great Job</Typography>
          <Typography sx={styles.modalSubtitle}>
            You've successfully done your contribution
          </Typography>
          <Typography
            sx={{
              fontSize: "16px",
              fontWeight: "bold",
              textAlign: "center",
              mb: 1,
            }}
          >
            How was your experience?
          </Typography>
          <Typography
            sx={{ fontSize: "14px", textAlign: "center", color: "#666", mb: 2 }}
          >
            Your feedback helps us improve and serve you better
          </Typography>

          <Box sx={{ display: "flex", justifyContent: "center", mb: 3 }}>
            <Rating
              value={rating}
              onChange={(_, newValue) => setRating(newValue)}
              size="large"
              sx={{
                "& .MuiSvgIcon-root": {
                  color: "#F2B12F",
                },
              }}
            />
          </Box>
          {/* <Typography sx={{ fontSize: "14px", fontWeight: "medium", mb: 1 }}>
            Tell us more
          </Typography> */}
          <TextField
            fullWidth
            multiline
            rows={3}
            placeholder="Tell us more"
            value={feedback}
            onChange={(e) => setFeedback(e.target.value)}
            variant="outlined"
            inputProps={{
              maxLength: 249,
            }}
            sx={styles.feedbackInput}
          />
          <Button
            variant="contained"
            sx={styles.submitFeedbackButton}
            onClick={handleSubmit}
            disabled={!rating || isSubmitting}
          >
            {isSubmitting ? "Submitting..." : "Submit"}
          </Button>
        </Box>
      </Box>
    </Modal>
  );
};